import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Business from './pages/Business/Business';
import Forms from './pages/Forms/Forms';
import Audits from './pages/Audits/Audits';
import Reports from './pages/Reports/Reports';
import Users from './pages/Users/Users';
import Profile from './pages/Profile/Profile';

// Tipos ya están definidos en AuthContext, no necesitamos redefinirlos aquí

// Componente de ruta protegida
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode; 
  roles?: string[] 
}> = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Componente principal
const AppContent: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        
        <Route 
          path="/business/*" 
          element={
            <ProtectedRoute roles={['admin', 'auditor', 'manager']}>
              <Business />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/forms/*" 
          element={
            <ProtectedRoute roles={['admin']}>
              <Forms />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/audits/*" element={<Audits />} />
        <Route path="/reports/*" element={<Reports />} />
        
        <Route 
          path="/users/*" 
          element={
            <ProtectedRoute roles={['admin']}>
              <Users />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/profile" element={<Profile />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;